/**
 * The local recovery of a lost card from every anchor the card carries, checked
 * together against the whole body the add-in already reads: no server, no model.
 *
 * A card stores where each of its paragraphs sat (`CardParagraphAnchor`: Word's
 * paraId, the heading path, the index, the position, the neighbours' texts),
 * and a read in this session adds the same facts for what it read. The memory
 * folds both into one span anchor (`AnchorMemory.toSpanAnchor`), the same anchor
 * the server step sends. This step reads the body once and checks that anchor
 * against every paragraph.
 *
 * THE FIVE CHECKS, per span member, in the order that settles it
 *   1. id        the card's paraId is on exactly one body paragraph       -> that paragraph
 *   2. index     the paragraph at the card's index reads as the card's text -> that paragraph
 *   3. agreement exactly one paragraph whose heading path, neighbours and
 *                text all agree                                            -> that paragraph
 *   4. text      exactly one paragraph reads as the card's text            -> that paragraph
 *   5. position  several read as the text; exactly one sits within the
 *                position window of where the card sat                    -> that paragraph
 *   none         the places that still fit are reported as a tie, and the message says
 *                which of the five disagreed; nothing fits -> not found, same message
 *
 * Every text comparison goes through the engine's one reading
 * (`WordTokenizer.readsTheSame`); nothing is split or folded here. `textId`
 * carries no weight: Word for Mac exports `77777777` on every paragraph.
 *
 * WHY together rather than one tier after another
 *   A reopened document has not shifted: its ids may be new (Word for Mac mints
 *   them for a file it did not save itself) while index, heading, neighbours and
 *   text all still agree, so no single signal is the point and none is last.
 */
import { ParagraphLocator, type ParagraphAnchor } from "@common/utils/redline/paragraph-locator";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import { ON_PARAGRAPH } from "@common/utils/redline/wire/redline-placement";

import type { RecoveryContext, RecoveryStep } from "./recovery-chain";
import { RecoveryResult, type AmbiguousCandidate, type AnchorChecks } from "./recovery-result";
import type { BodyAnchorParagraph, WordParagraph } from "../word-document";

/** The scale of `positionPct` on every side: a position as a percentage of the body. */
const PERCENT = 100;

/** What one span member's anchors say about one body paragraph. */
interface AnchorAgreement {
    walkIndex: number;
    idAgrees: boolean;
    indexAgrees: boolean;
    textAgrees: boolean;
    headingAgrees: boolean;
    /** Null when the member names no neighbour on either side. */
    neighboursAgree: boolean | null;
    /** Null when the member carries no position. */
    positionDeltaPct: number | null;
}

/** One member settled on one body paragraph, the check that settled it, and what every check said. */
interface SettledMember {
    settled: true;
    walkIndex: number;
    settledBy: string;
    checks: AnchorChecks;
}

/** One member no check settled: the places that still fit, why the checks disagreed, and what every check said. */
interface UnsettledMember {
    settled: false;
    disagreement: string;
    placesThatFit: AnchorAgreement[];
    checks: AnchorChecks;
}

type MemberVerdict = SettledMember | UnsettledMember;

/** What each check found for one member, before a verdict is drawn from it. */
interface MemberFindings {
    byId: AnchorAgreement[];
    atIndex: AnchorAgreement | undefined;
    byText: AnchorAgreement[];
    withinPositionWindow: AnchorAgreement[];
}

export class CardAnchorStep implements RecoveryStep {
    readonly tier = "card-anchor";
    readonly triggers = "any";

    async recover({ memory, body: recoveryBody }: RecoveryContext): Promise<RecoveryResult> {
        const members = memory.toSpanAnchor().members;
        const body = recoveryBody.anchorParagraphs;
        const verdicts = members.map((member) => CardAnchorStep.settle(member, body));
        const unsettled = verdicts.find((verdict): verdict is UnsettledMember => !verdict.settled);
        /**
         * CASES
         *   - a member no check settled, with places that fit -> a tie naming them and the disagreement
         *   - a member no check settled, nothing fits         -> not found, with the disagreement
         *   - every member settled, in one contiguous run    -> recovered on those paragraphs
         *   - every member settled, apart from each other    -> not found: the span is broken
         */
        if (unsettled !== undefined) {
            if (unsettled.placesThatFit.length === 0) return RecoveryResult.notFoundAfterChecks(unsettled.disagreement, unsettled.checks);
            return RecoveryResult.ambiguousAfterChecks(unsettled.disagreement, unsettled.placesThatFit.map(CardAnchorStep.candidateOf), unsettled.checks);
        }
        const settledMembers = verdicts.filter((verdict): verdict is SettledMember => verdict.settled);
        /** The span's first paragraph speaks for the span in the Details view. */
        const checks = settledMembers[0]?.checks ?? CardAnchorStep.NO_MEMBER_CHECKS;
        const walkIndexes = settledMembers.map((verdict) => verdict.walkIndex);
        const contiguous = walkIndexes.every((walkIndex, position) => position === 0 || walkIndex === walkIndexes[position - 1] + 1);
        if (!contiguous) return RecoveryResult.notFoundAfterChecks(`the card's paragraphs were found apart from each other, at body indexes ${walkIndexes.join(", ")}`, checks);
        const paragraphs = walkIndexes.map((walkIndex) => body[walkIndex]?.paragraph ?? null);
        const unpaired = paragraphs.some((paragraph) => paragraph === null);
        if (unpaired) return RecoveryResult.notFoundAfterChecks("the paragraph found is one Word lists no paragraph for", checks);
        const settledBy = Array.from(new Set(settledMembers.map((verdict) => verdict.settledBy))).join(", ");
        return RecoveryResult.recovered({
            paragraphs: paragraphs.filter((paragraph): paragraph is WordParagraph => paragraph !== null),
            placement: ON_PARAGRAPH,
            explanation: `card anchors agree: ${settledBy}`,
            checks,
        });
    }

    /** The checks of a span with no members at all: nothing was stored, nothing could be checked. */
    private static readonly NO_MEMBER_CHECKS: AnchorChecks = { id: "not stored", indexAndText: "not stored", headingPath: "not stored", neighbours: "not stored", position: "not stored" };

    /** One member checked against every body paragraph, settled by the first check that names one paragraph. */
    private static settle(member: ParagraphAnchor, body: BodyAnchorParagraph[]): MemberVerdict {
        const memberHasText = WordTokenizer.collapse(member.text).length > 0;
        const agreements = body.map((paragraph) => CardAnchorStep.agreementOf(member, paragraph, body));
        const byId = agreements.filter((agreement) => agreement.idAgrees);
        const atIndex = member.paragraphIndex === null ? undefined : agreements[member.paragraphIndex];
        const byText = memberHasText ? agreements.filter((agreement) => agreement.textAgrees) : [];
        const withinPositionWindow = byText.filter((agreement) => agreement.positionDeltaPct === null || agreement.positionDeltaPct <= ParagraphLocator.POSITION_PRUNE_WINDOW_PCT);
        const findings: MemberFindings = { byId, atIndex, byText, withinPositionWindow };
        const settledOn = (chosen: AnchorAgreement, settledBy: string): SettledMember => ({
            settled: true,
            walkIndex: chosen.walkIndex,
            settledBy,
            checks: CardAnchorStep.checksOf(member, memberHasText, findings, chosen),
        });
        if (byId.length === 1) return settledOn(byId[0], "id");
        if (atIndex !== undefined && atIndex.textAgrees) return settledOn(atIndex, "index and text");
        const byEverything = agreements.filter((agreement) => agreement.textAgrees && agreement.headingAgrees && agreement.neighboursAgree === true);
        if (byEverything.length === 1) return settledOn(byEverything[0], "heading path, neighbours and text");
        if (byText.length === 1) return settledOn(byText[0], "text");
        if (byText.length > 1 && withinPositionWindow.length === 1) return settledOn(withinPositionWindow[0], "text, one place within the position window");
        return {
            settled: false,
            disagreement: CardAnchorStep.disagreementOf(member, memberHasText, findings),
            placesThatFit: withinPositionWindow.length > 0 ? withinPositionWindow : byText,
            checks: CardAnchorStep.checksOf(member, memberHasText, findings, null),
        };
    }

    /**
     * One short answer per check, for the card's Details view.
     *
     * CASES, per check
     *   - the card never stored that anchor            -> "not stored"
     *   - a paragraph was chosen                       -> "agrees" or "differs: …" at that paragraph
     *   - no paragraph was chosen                      -> how many of the places reading as the text it agrees at
     *
     * EXAMPLE, a reopened copy whose ids Word minted anew, settled by index and text
     *   id "differs: the card's id is not in the document", indexAndText "agrees", headingPath "agrees",
     *   neighbours "agrees", position "0% from where the card sat"
     */
    private static checksOf(member: ParagraphAnchor, memberHasText: boolean, findings: MemberFindings, chosen: AnchorAgreement | null): AnchorChecks {
        const { byId, atIndex, byText, withinPositionWindow } = findings;
        const agreesAt = (agrees: (agreement: AnchorAgreement) => boolean): string => `agrees at ${byText.filter(agrees).length} of ${byText.length} places reading as the text`;
        const id = member.paraId === null
            ? "not stored"
            : byId.length === 1
                ? "agrees"
                : byId.length === 0
                    ? "differs: the card's id is not in the document"
                    : `differs: the card's id is on ${byId.length} paragraphs`;
        const indexAndText = member.paragraphIndex === null
            ? "not stored"
            : !memberHasText
                ? "not stored: the card carries no text"
                : atIndex === undefined
                    ? `differs: card index ${member.paragraphIndex} is past the end of the document`
                    : atIndex.textAgrees
                        ? "agrees"
                        : `differs: the paragraph at card index ${member.paragraphIndex} reads differently`;
        const headingPath = member.headingPath === null
            ? "not stored"
            : chosen !== null
                ? chosen.headingAgrees ? "agrees" : "differs: the paragraph found sits under another heading"
                : agreesAt((agreement) => agreement.headingAgrees);
        const neighbours = member.neighbourAbove === null && member.neighbourBelow === null
            ? "not stored"
            : chosen !== null
                ? chosen.neighboursAgree === true ? "agrees" : "differs: the paragraphs beside the one found read differently"
                : agreesAt((agreement) => agreement.neighboursAgree === true);
        const position = member.positionPct === null
            ? "not stored"
            : chosen !== null && chosen.positionDeltaPct !== null
                ? `${Math.round(chosen.positionDeltaPct)}% from where the card sat`
                : `${withinPositionWindow.length} of ${byText.length} places reading as the text within the position window`;
        return { id, indexAndText, headingPath, neighbours, position };
    }

    /**
     * The five checks of one member against one body paragraph.
     *
     * EXAMPLE
     *   member     paraId 4FBD3405, index 104, position 16.9, heading ["Main Agreement", "1 R27"], above "", below "OPEN: When?"
     *   paragraph  walk index 104 of 616, paraId 4FBD3405, text "Ex. 3", heading ["Main Agreement", "1 R27"]
     *   -> idAgrees true, indexAgrees true, textAgrees true, headingAgrees true,
     *      neighboursAgree from the paragraphs at 103 and 105, positionDeltaPct |100 * 104 / 616 - 16.9| = 0
     */
    private static agreementOf(member: ParagraphAnchor, paragraph: BodyAnchorParagraph, body: BodyAnchorParagraph[]): AnchorAgreement {
        return {
            walkIndex: paragraph.walkIndex,
            idAgrees: member.paraId !== null && paragraph.paraId === member.paraId,
            indexAgrees: member.paragraphIndex === paragraph.walkIndex,
            textAgrees: WordTokenizer.readsTheSame(paragraph.text, member.text),
            headingAgrees: CardAnchorStep.headingPathsAgree(member.headingPath, paragraph.headingPath),
            neighboursAgree: CardAnchorStep.neighboursAgree(member, paragraph.walkIndex, body),
            positionDeltaPct: member.positionPct === null ? null : Math.abs((PERCENT * paragraph.walkIndex) / body.length - member.positionPct),
        };
    }

    /**
     * Whether two heading paths read the same, segment by segment.
     *
     * CASES
     *   - neither side has a path   -> agree: the card sat under no heading, and so does the paragraph
     *   - exactly one side has one  -> differ: one sits under a heading, the other before any
     *   - both have one             -> the same length and every segment reading the same
     */
    private static headingPathsAgree(memberPath: string[] | null, paragraphPath: string[] | null): boolean {
        if (memberPath === null && paragraphPath === null) return true;
        if (memberPath === null || paragraphPath === null) return false;
        if (memberPath.length !== paragraphPath.length) return false;
        return memberPath.every((segment, depth) => WordTokenizer.readsTheSame(segment, paragraphPath[depth]));
    }

    /**
     * Whether the paragraphs either side of a body paragraph read as the member's neighbours.
     *
     * CASES
     *   - the member names a neighbour on a side -> that side must read the same; a body edge
     *                                               (no paragraph there) disagrees
     *   - the member names none on a side        -> that side is not checked
     *
     * NULL
     *   The member names no neighbour on either side: nothing to check.
     */
    private static neighboursAgree(member: ParagraphAnchor, walkIndex: number, body: BodyAnchorParagraph[]): boolean | null {
        const sides = [
            { expected: member.neighbourAbove, actual: body[walkIndex - 1] },
            { expected: member.neighbourBelow, actual: body[walkIndex + 1] },
        ].filter((side) => side.expected !== null);
        if (sides.length === 0) return null;
        return sides.every((side) => side.actual !== undefined && side.expected !== null && WordTokenizer.readsTheSame(side.actual.text, side.expected.text));
    }

    /** One line naming what each of the five checks found, for the user's message and the log. */
    private static disagreementOf(member: ParagraphAnchor, memberHasText: boolean, found: MemberFindings): string {
        const idState = member.paraId === null ? "the card carries no id" : found.byId.length === 0 ? "the id is not in the document" : `the id is on ${found.byId.length} paragraphs`;
        const indexState = member.paragraphIndex === null
            ? "the card carries no index"
            : found.atIndex === undefined
                ? `index ${member.paragraphIndex} is past the end of the document`
                : `the paragraph at index ${member.paragraphIndex} reads differently`;
        const textState = !memberHasText ? "the card carries no text" : found.byText.length === 0 ? "no paragraph reads as the text" : `${found.byText.length} paragraphs read as the text`;
        const headingState = member.headingPath === null
            ? "the card carries no heading path"
            : `the heading path agrees at ${found.byText.filter((agreement) => agreement.headingAgrees === true).length} of them`;
        const neighbourState = member.neighbourAbove === null && member.neighbourBelow === null
            ? "the card carries no neighbours"
            : `the neighbours agree at ${found.byText.filter((agreement) => agreement.neighboursAgree === true).length} of them`;
        const positionState = member.positionPct === null ? "the card carries no position" : `${found.withinPositionWindow.length} within the position window`;
        return `card anchors disagree: ${idState}; ${indexState}; ${textState}; ${headingState}; ${neighbourState}; ${positionState}`;
    }

    /** A place that still fits, as the user's choice names it: its body index and which checks agreed there. */
    private static candidateOf(agreement: AnchorAgreement): AmbiguousCandidate {
        const agreed = [
            agreement.headingAgrees === true ? "heading path agrees" : agreement.headingAgrees === false ? "heading path differs" : null,
            agreement.neighboursAgree === true ? "neighbours agree" : agreement.neighboursAgree === false ? "neighbours differ" : null,
            agreement.positionDeltaPct === null ? null : `${Math.round(agreement.positionDeltaPct)}% from where the card sat`,
        ].filter((part): part is string => part !== null);
        return { paragraphIndex: agreement.walkIndex, anchorPhrase: null, reason: agreed.join(", ") };
    }
}
